import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Campus IT room management (delegated). Admin sees all campuses; IT staff their own.
export default function Rooms() {
  const { user, isAdmin } = useAuth();
  const [campuses, setCampuses] = useState([]);
  const [campus, setCampus] = useState('');
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);   // {id?, code, name, campus_id}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/master/campuses').then(r => {
      const list = r.data.filter(c => c.active && (isAdmin || (user.campus_ids||[]).includes(c.id)));
      setCampuses(list);
      setCampus(String((user.campus_ids||[])[0] || list[0]?.id || ''));
    }).catch(()=>{});
  }, [isAdmin, user]);

  const load = useCallback(() => {
    if (!campus) return;
    api.get(`/master/rooms/${campus}`).then(r => setRows(r.data)).catch(()=>{});
  }, [campus]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setModal({ code:'', name:'', campus_id: campus });
  const openEdit = r => setModal({ id:r.id, code:r.code||'', name:r.name, campus_id:String(r.campus_id), active:r.active });
  const save = async () => {
    if (!modal.name.trim()) return;
    setBusy(true);
    try {
      if (modal.id) await api.put(`/master/rooms/${modal.id}`, { name:modal.name.trim(), code:modal.code, campus_id: isAdmin ? parseInt(modal.campus_id) : undefined });
      else await api.post('/master/rooms', { campus_id: parseInt(campus), name:modal.name.trim(), code:modal.code });
      setModal(null); load();
    } catch(e) { alert(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  const del = async r => { if (window.confirm(`Deactivate room "${r.name}"?`)) { try { await api.delete(`/master/rooms/${r.id}`); load(); } catch(e){ alert(e.response?.data?.error||'Failed'); } } };
  const hardDel = async r => { if (window.confirm(`Permanently delete room "${r.name}"?\nThis cannot be undone. If the room is used by tickets or assets, deactivate it instead.`)) { try { await api.delete(`/master/rooms/${r.id}?hard=1`); load(); } catch(e){ alert(e.response?.data?.error||'Failed'); } } };

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Rooms</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Create and manage rooms for your campus. {isAdmin && 'As admin you can also transfer a room to another campus.'}</p>
      </div>

      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <select value={campus} onChange={e => setCampus(e.target.value)} style={{ padding:'6px 8px', minWidth:200 }}>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={openNew} disabled={!campus}>+ Add Room</button>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No rooms in this campus yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Code</th><th>Room Name</th><th>Status</th><th></th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} style={{ opacity: r.active ? 1 : 0.5 }}>
                <td className="mono" style={{ fontSize:12 }}>{r.code || '—'}</td>
                <td style={{ fontSize:13, fontWeight:500 }}>{r.name}</td>
                <td style={{ fontSize:12 }}>{r.active ? 'Active' : 'Inactive'}</td>
                <td><div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)}>Edit</button>
                  {r.active && <button className="btn btn-secondary btn-sm" onClick={() => del(r)}>Deactivate</button>}
                  <button className="btn btn-danger btn-sm" onClick={() => hardDel(r)}>Delete</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-header"><span className="modal-title">{modal.id?'Edit':'Add'} Room</span><button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-group"><label>Room Code</label><input value={modal.code} onChange={e => setModal(m => ({ ...m, code:e.target.value.toUpperCase() }))} placeholder="e.g. A102" /></div>
            <div className="form-group"><label>Room Name *</label><input value={modal.name} onChange={e => setModal(m => ({ ...m, name:e.target.value }))} placeholder="e.g. Grade 6A Classroom" /></div>
            {isAdmin && modal.id && (
              <div className="form-group"><label>Campus (transfer)</label>
                <select value={modal.campus_id} onChange={e => setModal(m => ({ ...m, campus_id:e.target.value }))}>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy || !modal.name.trim()}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
